/**
 * Which view of an agentic-logic surface is on screen, carried in the URL's
 * query string rather than in its path.
 *
 * Every agentic-logic view is one route per surface, because the Atlas chat
 * rail is a sibling of the page inside the layout shell and a rule build spans
 * two of these views: the author starts it on the creation view and lands on
 * the rule it produced. Sibling *routes* tear the page subtree down and back up
 * across that hand-off; a query param leaves the route element — and therefore
 * the live chat session, its transcript, and its open stream — mounted.
 *
 * - absent → the rules list
 * - `new` → the rule creation flow
 * - anything else → that rule's workspace
 *
 * These primitives live apart from `surface.ts` so the embedded-path helpers
 * can read them without importing the surface descriptors, which import back.
 */
export const RULE_QUERY_PARAM = "rule";
export const NEW_RULE_PARAM_VALUE = "new";

export type AgenticLogicView =
  | { kind: "list" }
  | { kind: "new" }
  | { kind: "rule"; ruleId: string };

/**
 * Reads the current view out of a location's search string. `ruleParam` names
 * the param to read; embedded surfaces namespace theirs away from the host's
 * own query string (see `deriveEmbeddedPaths`).
 */
export const resolveAgenticLogicView = (
  search: string | URLSearchParams,
  ruleParam: string = RULE_QUERY_PARAM
): AgenticLogicView => {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;
  const requested = params.get(ruleParam);
  if (!requested) {
    return { kind: "list" };
  }
  if (requested === NEW_RULE_PARAM_VALUE) {
    return { kind: "new" };
  }
  return { kind: "rule", ruleId: requested };
};
